"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect, useState } from "react";
import type { Locale } from "@/i18n/routing";

// global-error.tsx renders outside the [locale] tree, so next-intl is
// unavailable here. Keep the 5-language copy inline.
const COPY = {
  ko: {
    title: "문제가 발생했습니다",
    body: "예상치 못한 오류가 발생했습니다. 다시 시도해주세요.",
    offline: "네트워크 연결을 확인해주세요.",
    retry: "다시 시도",
    signIn: "다시 로그인",
  },
  en: {
    title: "Something went wrong",
    body: "An unexpected error occurred. Please try again.",
    offline: "Please check your network connection.",
    retry: "Try again",
    signIn: "Sign in again",
  },
  ja: {
    title: "問題が発生しました",
    body: "予期しないエラーが発生しました。もう一度お試しください。",
    offline: "ネットワーク接続を確認してください。",
    retry: "再試行",
    signIn: "再ログイン",
  },
  "zh-TW": {
    title: "發生問題",
    body: "發生未預期的錯誤，請再試一次。",
    offline: "請檢查您的網路連線。",
    retry: "重試",
    signIn: "重新登入",
  },
  "zh-CN": {
    title: "出现问题",
    body: "出现意外错误，请重试。",
    offline: "请检查您的网络连接。",
    retry: "重试",
    signIn: "重新登录",
  },
} as const satisfies Record<
  Locale,
  { title: string; body: string; offline: string; retry: string; signIn: string }
>;

type LocaleKey = Locale;

function detectLocale(): LocaleKey {
  if (typeof document === "undefined") return "ko";
  const lang = (
    document.documentElement.lang ||
    (typeof navigator !== "undefined" ? navigator.language : "") ||
    "ko"
  ).toLowerCase();
  if (lang.startsWith("ko")) return "ko";
  if (lang.startsWith("ja")) return "ja";
  if (lang.startsWith("zh-tw") || lang.startsWith("zh-hant")) return "zh-TW";
  if (lang.startsWith("zh")) return "zh-CN";
  return "en";
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [locale, setLocale] = useState<LocaleKey>("ko");
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    Sentry.captureException(error);
    // SSR has no document/navigator, so we must defer locale + onLine reads
    // to a post-mount setState. The transient render with defaults ("ko" /
    // online) is acceptable for this emergency screen — it flickers once,
    // then settles on the user's real locale and network state.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocale(detectLocale());
    if (typeof navigator !== "undefined") {
      setIsOffline(!navigator.onLine);
      const onOnline = () => setIsOffline(false);
      const onOffline = () => setIsOffline(true);
      window.addEventListener("online", onOnline);
      window.addEventListener("offline", onOffline);
      return () => {
        window.removeEventListener("online", onOnline);
        window.removeEventListener("offline", onOffline);
      };
    }
  }, [error]);

  const msg = error.message ?? "";
  const digest = error.digest ?? "";

  // Offline guard for every reload path: if the user is offline, never reload —
  // keep the current document so they don't lose the page they were on. Once
  // back online they can hit "Try again" themselves.
  const canReload =
    typeof window !== "undefined" &&
    (typeof navigator === "undefined" || navigator.onLine !== false);

  // Navigation interruptions: in-flight server action races a notFound() /
  // redirect(). Limit to one reload per session so a transient network failure
  // surfacing as "unexpected response" can't trap us in a reload loop.
  const isNavigation =
    msg.includes("unexpected response") ||
    msg.includes("NEXT_NOT_FOUND") ||
    msg.includes("NEXT_REDIRECT") ||
    digest.includes("NEXT_NOT_FOUND") ||
    digest.includes("NEXT_REDIRECT");

  if (isNavigation && canReload) {
    const RELOAD_KEY = "__navReloadAttempt";
    if (!sessionStorage.getItem(RELOAD_KEY)) {
      sessionStorage.setItem(RELOAD_KEY, "1");
      window.location.reload();
      return null;
    }
  }

  // Transient auth failures: a stale in-memory token surfaces as one of these
  // strings when supabase-js can't refresh in time. Reload once so proxy.ts
  // can re-mint the cookie via the refresh token; if the same error fires
  // again we fall through to the normal error UI instead of looping.
  const isAuthRecoverable =
    msg.includes("AuthSessionMissingError") ||
    msg.includes("JWT expired") ||
    msg.includes("Invalid Refresh Token") ||
    msg.includes("refresh_token_not_found") ||
    digest.startsWith("DYNAMIC_SERVER_USAGE");

  if (isAuthRecoverable && canReload) {
    const RELOAD_KEY = "__authReloadAttempt";
    if (!sessionStorage.getItem(RELOAD_KEY)) {
      sessionStorage.setItem(RELOAD_KEY, "1");
      window.location.reload();
      return null;
    }
  }

  // Transient Node.js Web Streams glitch on Next.js 16 + Node 22.
  const isStreamRecoverable =
    msg.includes("transformAlgorithm") || msg.includes("kState");
  if (isStreamRecoverable && canReload) {
    const RELOAD_KEY = "__streamReloadAttempt";
    if (!sessionStorage.getItem(RELOAD_KEY)) {
      sessionStorage.setItem(RELOAD_KEY, "1");
      window.location.reload();
      return null;
    }
  }

  const showSignInAgain =
    isAuthRecoverable &&
    typeof window !== "undefined" &&
    sessionStorage.getItem("__authReloadAttempt") === "1";

  const t = COPY[locale];

  return (
    <html lang={locale}>
      <body>
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="w-full max-w-md space-y-4 text-center">
            <h2 className="text-2xl font-bold">{t.title}</h2>
            <p className="text-gray-600">{isOffline ? t.offline : t.body}</p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              <button
                onClick={reset}
                className="rounded-lg bg-orange-600 px-6 py-3 text-white hover:bg-orange-700"
              >
                {t.retry}
              </button>
              {showSignInAgain && (
                <button
                  onClick={() => {
                    if (typeof window !== "undefined") {
                      sessionStorage.removeItem("__authReloadAttempt");
                      sessionStorage.removeItem("__sessionRedirected");
                      window.location.assign("/login");
                    }
                  }}
                  className="rounded-lg border border-orange-600 px-6 py-3 text-orange-600 hover:bg-orange-50"
                >
                  {t.signIn}
                </button>
              )}
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
